// ** React Imports **
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ** MUI Components (only Dialog and IconButton) **
import {
  Dialog,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";

// ** Redux **
import { useDispatch, useSelector } from "react-redux";
import { verifyOtp, resendOtp } from "src/redux/actions/AuthAction";

// ** Context **
import { useAuthModal } from "src/context/AuthModalContext";

// ** Snackbar **
import { useSnackbar } from 'notistack';

// ** Components **
import LoadingOverlay from "../authentication/LoadingOverlay";

// ** Styles **
import './LoginModal.css';

/**
 * OtpVerificationModal Component
 * 
 * Modal popup for OTP verification
 * Matches the design of LoginModal
 */

const OtpVerificationModal = () => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loadingBtnLoader, forgotPasswordEmail } = useSelector((state) => state.auth);
  
  // Auth modal context
  const { 
    isOtpModalOpen, 
    closeModals, 
    switchToForgotPassword,
    openResetPasswordModal,
  } = useAuthModal();

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0 && isOtpModalOpen) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer, isOtpModalOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOtpModalOpen) {
      setOtp(["", "", "", "", "", ""]);
      setResendTimer(60);
      setCanResend(false);
      setIsLoading(false);
    }
  }, [isOtpModalOpen]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedData.length === 6) {
      const newOtp = pastedData.split("").slice(0, 6);
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      enqueueSnackbar(t('pleaseEnterComplete6DigitOtp'), {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await dispatch(verifyOtp({ otp: otpString }));
      
      if (result.type === "auth/verifyOtp/fulfilled") {
        enqueueSnackbar(t('otpVerifiedSuccessfully'), {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        openResetPasswordModal();
      } else {
        enqueueSnackbar(result.payload || t('invalidOtp'), {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("Error occurred:", error);
      enqueueSnackbar(t('somethingWentWrong'), {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    try {
      const result = await dispatch(resendOtp());
      
      if (result.type === "auth/resendOtp/fulfilled") {
        enqueueSnackbar(t('otpSent'), {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setResendTimer(60);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        enqueueSnackbar(result.payload || t('failedToResendOtp'), {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } catch (error) {
      enqueueSnackbar(t('failedToResendOtp'), {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (!loadingBtnLoader && !isLoading) {
      closeModals();
    }
  };

  return (
    <Dialog
      open={isOtpModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          maxWidth: 480,
          backgroundColor: 'white',
        },
      }}
    >
      <div style={{ position: 'relative', padding: '3rem 2.5rem' }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          disabled={loadingBtnLoader || isLoading}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Loading Overlay */}
        <LoadingOverlay isVisible={loadingBtnLoader || isLoading} type="otpVerification" />

        {/* Card Title */}
        <h2 className="login-page-new__title">{t('otpVerification')}</h2>

        <p style={{ 
          textAlign: 'center', 
          color: '#666', 
          marginBottom: '2rem',
          fontSize: '0.938rem',
          lineHeight: '1.5'
        }}>
          {t('enterVerificationCodeSentTo')}{' '}
          <span style={{ color: '#333', fontWeight: 600 }}>
            {forgotPasswordEmail || ''}
          </span>
        </p>

        {/* OTP Input */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            marginBottom: "1.5rem",
          }}
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                if (el) inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              maxLength={1}
              disabled={isLoading || loadingBtnLoader}
              style={{
                width: "48px",
                height: "56px",
                textAlign: "center",
                fontSize: "24px",
                fontWeight: 600,
                border: "1px solid #ddd",
                borderRadius: "12px",
                outline: "none",
                transition: "all 0.2s ease",
                backgroundColor: (isLoading || loadingBtnLoader) ? '#f5f5f5' : 'white',
              }}
              onFocus={(e) => {
                if (!isLoading && !loadingBtnLoader) {
                  e.target.style.borderColor = "#6c3fb5";
                  e.target.style.boxShadow = "0 0 0 3px rgba(108, 63, 181, 0.1)";
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#ddd";
                e.target.style.boxShadow = "none";
              }}
            />
          ))}
        </div>

        {/* Verify Button */}
        <button
          type="button"
          onClick={handleVerifyOTP}
          className="login-page-new__submit-btn"
          disabled={isLoading || loadingBtnLoader || otp.join("").length !== 6}
          style={{
            marginTop: "0.5rem",
            marginBottom: "1.5rem",
          }}
        >
          {isLoading || loadingBtnLoader ? t('verifying') : t('verify')}
        </button>

        {/* Resend Link */}
        <div style={{ textAlign: "center", fontSize: "0.938rem", color: "#666", marginBottom: "1rem" }}>
          <span>{t('didntReceiveCode')} </span>
          <button
            type="button"
            onClick={canResend && !isLoading && !loadingBtnLoader ? handleResendOTP : undefined}
            disabled={!canResend || isLoading || loadingBtnLoader}
            className="login-page-new__signup-link-text"
            style={{
              background: "none",
              border: "none",
              cursor: canResend && !isLoading && !loadingBtnLoader ? "pointer" : "default",
              opacity: canResend ? 1 : 0.6,
              padding: 0,
            }}
          >
            {canResend ? t('resend') : t('resendInCounter', { count: resendTimer })}
          </button>
        </div>

        {/* Back Link */}
        <div className="login-page-new__signup-link">
          <button 
            onClick={switchToForgotPassword} 
            disabled={loadingBtnLoader || isLoading}
            className="login-page-new__signup-link-text"
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: (loadingBtnLoader || isLoading) ? 'not-allowed' : 'pointer',
              opacity: (loadingBtnLoader || isLoading) ? 0.6 : 1,
            }}
          >
            {t('backToForgotPassword')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default OtpVerificationModal;
